import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { UserX, Loader2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blank = () => ({ first_name: '', last_name: '', email: '', job_title: '', phone: '', send_optin: true });

const ReplaceContactDialog = ({ contact, open, onOpenChange, onDone }) => {
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(blank()); }, [open]);

  const submit = async () => {
    if (!form.first_name.trim()) { toast.error('Enter the new contact\u2019s first name'); return; }
    if (!form.email.trim() || !form.email.includes('@')) { toast.error('Enter a valid email'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/contacts/${contact.id}/replace`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || null,
        email: form.email.trim(),
        job_title: form.job_title.trim() || null,
        phone: form.phone.trim() || null,
        send_optin: form.send_optin,
        base_url: window.location.origin,
      });
      toast.success(
        res.data.optin_sent
          ? `${form.first_name} added \u2014 opt-in email sent`
          : `${form.first_name} added on the same company`
      );
      onOpenChange(false);
      if (onDone) onDone(res.data.new_contact);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to replace contact');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-lg" data-testid="replace-contact-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <UserX className="w-5 h-5 text-amber-500" /> Mark as left &amp; replace
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            <b className="text-slate-300">{contact?.full_name}</b> will be archived (kept for history, badged "Left company" and never emailed again). Enter the person who replaces them on <b className="text-slate-300">{contact?.company_name || contact?.company?.name || 'the same company'}</b>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">First name *</label>
              <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="replace-first-name" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Last name</label>
              <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="replace-last-name" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Email *</label>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="new.person@company.com" className="bg-slate-900 border-slate-700 text-white" data-testid="replace-email" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Job title</label>
              <Input value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="replace-job-title" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Phone</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="replace-phone" />
            </div>
          </div>
          <label className="flex items-start gap-2 cursor-pointer bg-slate-900 border border-slate-800 rounded-md px-3 py-2.5" data-testid="replace-send-optin-label">
            <input type="checkbox" checked={form.send_optin} onChange={e => setForm({ ...form, send_optin: e.target.checked })} className="mt-0.5 w-4 h-4 accent-violet-600" data-testid="replace-send-optin" />
            <span className="text-sm text-slate-300 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-violet-600" /> Send the branded opt-in / preferences email now
            </span>
          </label>
          <p className="text-[11px] text-slate-500">The new contact inherits the company's industry sector. They stay "not confirmed" until they opt in via the preference centre.</p>
        </div>

        <DialogFooter>
          <Button variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-500 text-white" onClick={submit} disabled={saving} data-testid="replace-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserX className="w-4 h-4 mr-2" />} Archive &amp; replace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReplaceContactDialog;
